/* eslint-disable @typescript-eslint/no-explicit-any */
import apiClient, { IresponseData, IresponseDataPaginated } from "@/lib/api";

export enum EComboStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}

export interface ComboServiceItem {
  id: number;
  service: {
    id: number;
    name: string;
    icon: string;
    color: string;
  };
}

export interface Combo {
  id: number;
  name: string;
  price: number;
  duration: number;
  status: EComboStatus;
  comboServices?: ComboServiceItem[];
  createdAt: string;
}

export interface CreateComboRequest {
  name: string;
  price: number;
  duration: number;
  services: number[];
  notes?: string;
}

export interface UpdateComboRequest {
  name?: string;
  price?: number;
  duration?: number;
  status?: EComboStatus;
}

export interface ComboFilter {
  search?: string;
  status?: EComboStatus;
  page?: number;
  limit?: number;
}

function removeEmptyFields<T extends Record<string, any>>(obj: T): Partial<T> {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value !== "" && value !== null && value !== undefined) {
      acc[key] = value;
    }
    return acc;
  }, {} as any);
}

export const comboApi = {
  async create(data: CreateComboRequest): Promise<IresponseData<Combo>> {
    try {
      const response = await apiClient.post<IresponseData<Combo>>("/combos", data);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al crear el combo. Por favor, intenta de nuevo.",
      );
    }
  },

  async getAll(filter?: ComboFilter): Promise<IresponseDataPaginated<Combo[]>> {
    try {
      const params = new URLSearchParams();
      if (filter?.page) params.append("page", filter.page.toString());
      if (filter?.limit) params.append("limit", filter.limit.toString());
      if (filter?.status) params.append("status", filter.status);
      if (filter?.search) params.append("search", filter.search);

      const response = await apiClient.get<IresponseDataPaginated<Combo[]>>(
        "/combos",
        { params },
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al obtener los combos. Por favor, intenta de nuevo.",
      );
    }
  },

  async getById(id: number): Promise<IresponseData<Combo>> {
    try {
      const response = await apiClient.get<IresponseData<Combo>>(`/combos/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al obtener el combo. Por favor, intenta de nuevo.",
      );
    }
  },

  async update(id: number, data: UpdateComboRequest): Promise<IresponseData<Combo>> {
    try {
      const cleanData = removeEmptyFields(data);
      const response = await apiClient.put<IresponseData<Combo>>(
        `/combos/${id}`,
        cleanData,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al actualizar el combo. Por favor, intenta de nuevo.",
      );
    }
  },

  async remove(id: number): Promise<IresponseData<void>> {
    try {
      const response = await apiClient.delete<IresponseData<void>>(`/combos/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al eliminar el combo. Por favor, intenta de nuevo.",
      );
    }
  },
};
